/**
 * D-020 V2B — the A/D hotkey listener (LT2-014/015/016), with #609's
 * browser-interaction fence.
 *
 * Mounted once per hosting surface (ValueDashboard, GameDetail), gated by the
 * caller on lt2Access && lt2HotkeysEnabled. Semantics (packet §V2B; the
 * spoken source correction governs — NOT the handwritten exhibit):
 *
 *   Vertical team layout (moneyline): A = the visually TOP team's visible
 *     NO action, D = the visually BOTTOM team's visible NO action — focus is
 *     per GAME, and the two keys land on two different team markets, never a
 *     YES/NO pair within one market.
 *   Side-by-side two-sided ladder (spread/total): A = LEFT displayed
 *     column, D = RIGHT.
 *
 * Size = the visible one-click preset; price = EXACTLY the displayed
 * top-of-book action the focused card's resolver reports (the same value a
 * mouse click there would submit); the same submit-time guard applies via
 * `submitIntent` — identical path, identical `lt-` coid namespace.
 *
 * ── The fence (#609) ─────────────────────────────────────────────────────
 * This handler is the OUTERMOST of three fences, not the last one:
 * `evaluateIntentGuard` re-validates identity/availability/price/role against
 * the current book at submit time, and the relay enforces the LT2 grant and
 * the contract ceiling. What lives here is only the browser-interaction
 * question — "was this keystroke plausibly a deliberate order at a card the
 * trader is looking at?" — and it answers it by ownership and visibility, not
 * by guessing at page state.
 *
 * Rejections split two ways, and the line matters:
 *
 *   SILENT (never an order attempt in the first place, so a receipt line
 *   would be noise that trains the trader to ignore the strip): modifier
 *   chords including Shift, IME composition, key repeat, an open MODAL, a
 *   backgrounded tab, not armed — and an owned keystroke while NOT
 *   armed-and-focused. No focused card is silent for A/D/W/E, which aim at
 *   one visibly-badged card; Pull All is the exception (#802) and gets a
 *   truthful ack instead, because its name already implies its own target.
 *
 *   TRUTHFUL ACK (armed, deliberate, and refused — a silent drop here would
 *   leave the trader unable to tell "refused" from "sent"): the focused
 *   market is gone, the price row is not visible, the focused card is
 *   EXPANDED and therefore paints no price (#647 moneyline), no displayed
 *   price on that side, the line is not 'active', an earlier submission is
 *   unresolved — and an owned keystroke WHILE armed with a card focused,
 *   which is the one silence that misleads rather than merely declines.
 *
 * A refusal must name the state the trader is actually in, because the ack
 * doubles as the instruction for getting out of it. "Not visible" and
 * "expanded" both produce no usable price row, but their fixes are opposite:
 * scroll to the card, versus collapse the card sitting in plain sight. See
 * `expandedSuppressesPrice`.
 *
 * Only the "market is gone" case CLEARS the focus — the card no longer
 * exists. A merely off-screen card KEEPS its focus, so scrolling back
 * restores it without a re-click; so does an expanded one, so collapsing
 * restores it without a re-click either.
 *
 * Overlay handling is deliberately two-part, because "is this keystroke
 * mine?" and "is the card covered?" are different questions. Ownership walks
 * up from the event target, which catches everything a trader can type into
 * or press — including the interiors of portalled menus and popovers.
 * Modality is the only PAGE-level fact: a modal can be open with focus still
 * on <body> (nothing inside it clicked yet), and it physically covers the
 * board. It is read from `lt2FocusStore.modalOpenCount`, which atoms/Modal
 * maintains on mount/unmount — every LT2-reachable surface renders its
 * modals through that atom, and it is the only thing in the app that paints
 * a `data-modal-root`, an `aria-modal="true"` dialog, or a `<dialog>` (the
 * three selectors the pre-Deploy-C full-document query matched on every
 * keydown). A surface that grows its own modal must call `noteModalOpened`.
 * Non-modal popovers occlude nothing and must NOT suppress — the pre-#609
 * bare `[role="dialog"]` query killed A/D page-wide whenever any ⓘ
 * InfoTooltip (cardKit) or the AlertControlsMenu (aria-modal="false") was
 * open, while missing real modals that carry no role.
 *
 * The focused card is located by the marks its own rows paint —
 * `data-lt2-focused="<cardId>"` and `data-lt2-price` (see `focusedPriceRow`).
 * That coupling is deliberate: it keeps the focus store's signature free of
 * DOM, and it is pinned by SpreadsTotalsTable.lt2Focus.test.tsx so a row
 * refactor fails a test rather than silently disarming the fence. Every
 * unknown resolves to REFUSE — no rows, a row claiming another card, no or
 * multiple price rows, a shadow root we cannot see into, or an engine without
 * `checkVisibility`. "We could not tell" must never read as "it is visible".
 *
 * ── Pre-send cost (speed-demon Deploy C) ─────────────────────────────────
 * Everything between the keydown and the POST is perceived lag, so the path
 * reads facts the page PUBLISHED rather than measuring the page:
 *
 *   • the binding table is memoized on (mapping, grants) — the Map/Set the
 *     lookup built on every keystroke in the app is built once per change;
 *   • the row lookup is scoped to the focused card's root (published by
 *     useLt2Card at focus time) and falls back to the document only when no
 *     root is known or the root holds no rows;
 *   • "a row claims another card" is a store flag (`focusPaintAmbiguous`),
 *     kept by each card's post-commit effect with the very boolean its mark
 *     painters render from — the same verdict, without the document walk;
 *   • the visibility verdict is the row's IntersectionObserver flag when it
 *     is fresh (stamped within 1 s AND after the last scroll/resize); only a
 *     missing or stale flag pays `checkVisibility` + `getBoundingClientRect`,
 *     which force a synchronous style+layout flush on a board React
 *     re-renders every ≤33 ms — the single largest pre-send cost;
 *   • the in-flight lock is per market with an 8 s ceiling, so one hung
 *     POST wedges its own market, not every card's hotkeys until the
 *     socket times out.
 *
 * None of it relaxes a refusal: an absent flag is the old path, never a yes.
 *
 * ── Level-addressed execution (Sniper v2 Phase 2) ────────────────────────
 * `docs/260909SniperV2Design.md` §4.1: the focused card says WHICH card, the
 * level cursor (`lt2FocusStore.cursor`) says WHERE on it, and A/D/W/E/Q/R act
 * at the cursor's rung rather than only at top of book. The cursor cannot widen
 * what a key may do — it only chooses which rung the existing fences are run
 * against, and that rung needs TWO independent witnesses before an order is
 * built: the card's resolver must publish a displayed level there
 * (`Lt2HotkeyTarget.levels`) and the DOM row must be painting the same cents
 * (`data-lt2-level-price`). See `resolveCursorLevel`.
 *
 * The cursor keys themselves (J/K level, H/L book, 1–9/0 jump) plus the band's
 * view keys (Z market tab, ` dock) are NAVIGATION: they carry a `navigation`
 * entry in the registry, have no slot and no audit stamp, and are dispatched
 * before the order path. Arrows are NOT used — they stay reserved app-wide with
 * no route-scoped exception (Josh, 2026-09-09), which is also why `L` is a
 * cursor key here and the §6 latency dump moved to `Y`.
 *
 * ── The composer keys and the latency dump (Phase 3 / 4) ──────────────────
 * F / Shift+F / G / Shift+G fire the SEQUENCE COMPOSER's armed plan
 * (`stores/sequenceComposerStore.fireArmedSequence`) — one relay call per
 * press, audited per leg by the relay rather than by a single-order `__lt2`
 * stamp, which is why these actions carry no slot and no `inputMethod`. They are
 * nonetheless fenced exactly like A/D — armed, un-owned, no modal, visible tab,
 * a focused card, a live market, a visible price row on the CURSOR's book, the
 * cursor's rung proven by both witnesses, the per-market in-flight lock — and
 * then the store re-checks every grant/flag/ceiling and the pacing window
 * inside `fire()`. What a key may NOT do is arm: the op, its depth and its size
 * are the composer's, so a key whose op is not the armed one refuses and names
 * both, rather than firing a plan the trader did not point that key at.
 * `Y` copies the latency HUD's ring to the clipboard and touches no venue.
 *
 * S is the #724 two-sided press: it exists only for a trader who opted in, and
 * it posts the card's A and D joins together (see `fireTwoSided`) — at top of
 * book only; with the level cursor moved off it, S refuses. Z is the
 * market-tab cycle since Phase 2; X remains unbound. Q and R are the speed-demon
 * D1 JUMP keys: Q re-prices the trader's ONE resting order on A's slot to the
 * bid that slot displays, R does the same on D's slot — through
 * `oneClickStore.submitJumpIntent` → `accountStore.replaceOrder` → the relay's
 * `POST /orders/:id/replace` (one round trip). Every fence below applies to
 * Q/R exactly as to A/D; on top, the card's resolver must name exactly one
 * resting order on that slot (`Lt2HotkeyTarget.resting`) and the displayed
 * price must differ from where it rests — each refusal is a truthful ack.
 * SPACEBAR is the V3 focused-game Pull All:
 * opt-in via its own setting, consumed only while armed + aimed, and always
 * an entry point into the exact-game cancel machinery (lib/trading/pullAll)
 * — authoritative order state, fail-closed selection, frozen snapshot,
 * fenced per-order cancels — never a DOM sweep. W and E are the Live Trading V3 ATTACK keys:
 * the bounded aggressive siblings of A and D. W acts on the same slot A
 * posts on (top/left), E on D's slot (bottom/right) — but instead of joining
 * the displayed top-of-book, an attack submits ONE bounded limit at the
 * level ABOVE the displayed best same-side bid (take it when opposing
 * liquidity is displayed there, establish it when not), at the separate
 * attack size, postOnly false. Every fence below applies to W/E exactly as
 * it does to A/D, plus: the trader must hold the separate attack grant
 * (silent otherwise — the keys don't exist for them), and Attack must be
 * armed on top of one-click (truthful refusal otherwise). The relay
 * re-checks grant, ceiling, and depth on every attack order.
 */
import { useEffect } from 'react';
import { useOneClickStore, type PlacementReport } from '@/stores/oneClickStore';
import { useAttackStore } from '@/stores/attackStore';
import { useAuthStore } from '@/stores/authStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import {
  useLt2FocusStore,
  type Lt2CardTargets,
  type Lt2CursorSlot,
  type Lt2HotkeyTarget,
} from '@/stores/lt2FocusStore';
import type { Lt2AttackTarget } from '@/lib/trading/attackTarget';
import { executeFocusedGamePull, isPullAllPending } from '@/lib/trading/pullAll';
import type { LiveJumpIntent, LiveOrderIntent } from '@/lib/trading/liveIntent';
import { lt2ClockStamp, type Lt2ClockStamp } from '@/lib/trading/lt2Timing';
import { lineRefusalReason, twoSidedReadiness, type TwoSidedLeg } from '@/lib/trading/twoSidedReadiness';
import { twoSidedOptedIn } from '@/lib/trading/twoSidedOptIn';
import { probeMark, probeStart } from '@/lib/trading/orderLatencyProbe';
import {
  LIVE_HOTKEY_REGISTRY,
  actionForKey,
  type LiveHotkeyActionId,
  type LiveHotkeyGrants,
  hotkeyDisplayLabel,
  hotkeyPairLabel,
  hotkeyTokenFor,
  normalizeHotkey,
} from '@/lib/trading/liveHotkeyRegistry';
import { readOwnedHotkeys } from '@/hooks/useLiveHotkeys';
import {
  OP_LABEL,
  fireArmedSequence,
  useSequenceComposerStore,
  type SequenceOp,
} from '@/stores/sequenceComposerStore';
import { dumpLatencyCsv } from '@/views/SniperGame/cockpit/LatencyHud';

/**
 * Unresolved hotkey submissions, by market ticker (the card id when a target
 * carries no ticker) → epoch ms the POST left. A press is refused only while
 * ITS market's entry is younger than `LT2_INFLIGHT_TIMEOUT_MS`; an older
 * entry is a hung request and is treated as free (and dropped), so a socket
 * that never answers cannot wedge the keyboard until the browser gives up.
 * The relay's own duplicate guards (checkOrderLimit + trackIntent) remain the
 * authority on what actually reaches the venue.
 */
const _inFlight = new Map<string, number>();
export const LT2_INFLIGHT_TIMEOUT_MS = 8000;

function inFlightKeyFor(ticker: string, cardId: string): string {
  return ticker !== '' ? ticker : `card:${cardId}`;
}

function isInFlight(key: string, nowMs: number): boolean {
  const startedAt = _inFlight.get(key);
  if (startedAt == null) return false;
  if (nowMs - startedAt < LT2_INFLIGHT_TIMEOUT_MS) return true;
  _inFlight.delete(key); // stale: the request hung; the key is free again
  return false;
}

/** Marks the key busy; the returned release only clears OUR entry, so a
 *  request that settles late cannot free a newer one on the same market. */
function markInFlight(key: string, nowMs: number): () => void {
  _inFlight.set(key, nowMs);
  return () => {
    if (_inFlight.get(key) === nowMs) _inFlight.delete(key);
  };
}

/** How fresh a row's observer flag must be to stand in for a measurement. */
const LT2_VISIBILITY_FLAG_MAX_AGE_MS = 1000;

/**
 * The keydown's own time as a same-page dual-clock stamp (#849). `e.timeStamp`
 * is on the PERFORMANCE timeline — the same clock as `performance.now()`, the
 * monotonic half of every `Lt2ClockStamp` — and marks when the browser created
 * the event, before any handler ran, so queueing ahead of this handler counts
 * as lag. The stamp is the handler-entry `lt2ClockStamp()` moved back by that
 * queueing delay on BOTH halves: the monotonic half becomes the event time,
 * and the epoch half stays a `Date.now()` sample shifted by the same amount
 * (never `timeOrigin + ts`, whose drift from the wall clock over a long
 * session would trip the wall/monotonic disagreement check in lt2Elapsed).
 * A timestamp that is not a finite, positive, not-in-the-future performance
 * time (jsdom stamps events with an epoch value) falls back to the
 * handler-entry stamp unchanged.
 */
function eventClockStamp(e: Event): Lt2ClockStamp {
  const entry = lt2ClockStamp();
  const ts = e.timeStamp;
  if (Number.isFinite(ts) && ts > 0 && ts <= entry.monoMs) {
    const queuedMs = entry.monoMs - ts;
    return { monoMs: ts, wallMs: entry.wallMs - queuedMs, gen: entry.gen };
  }
  return entry;
}

/** What the in-flight hotkey press put on the wire, as its store call reports
 *  it: accepted venue ids, and whether any order's response was lost or came
 *  back without an id. */
interface PressPlacements {
  orders: Array<{ orderId: string; ticker: string }>;
  unknown: boolean;
}

/** One hotkey press on the wire. Pull All reads the presses still in flight
 *  so an order whose response lands after the pull's snapshot is swept by a
 *  second pass instead of being left resting on a "pulled" game (#724, main
 *  parity). StreamRift keeps several presses in flight at once (the lock is
 *  per market), so this is a set, not main's single slot. */
interface InFlightPress {
  startedAtMs: number;
  settled: Promise<void>;
  done: boolean;
  placements: PressPlacements;
}
const _inFlightPresses = new Set<InFlightPress>();

/** True while a Pull All is waiting on an in-flight press for its second
 *  pass; a second Spacebar then refuses as pending, like during the pull. */
let _pullWaiting = false;

/**
 * Mark `keys` (the press's markets) in flight and fire. `fire` is called
 * synchronously, after the per-market marks, so the store's fetch-first
 * dispatch is unchanged; the report it is handed only collects what the press
 * put on the wire, after its awaits. Every mark is released when the press
 * settles; each release clears only its own entry (see `markInFlight`).
 */
function trackInFlight(
  keys: readonly string[],
  nowMs: number,
  fire: (report: PlacementReport) => Promise<void>,
): void {
  const releases = [...new Set(keys)].map((k) => markInFlight(k, nowMs));
  const placements: PressPlacements = { orders: [], unknown: false };
  const report: PlacementReport = {
    placed: (id, ticker) => {
      if (id == null) placements.unknown = true;
      else placements.orders.push({ orderId: id, ticker });
    },
    lost: () => {
      placements.unknown = true;
    },
  };
  const press: InFlightPress = {
    startedAtMs: nowMs,
    settled: Promise.resolve(),
    done: false,
    placements,
  };
  press.settled = fire(report).finally(() => {
    press.done = true;
    for (const release of releases) release();
    _inFlightPresses.delete(press);
  });
  _inFlightPresses.add(press);
}

/** The presses a Pull All must wait for: still unsettled and younger than the
 *  in-flight ceiling. An older one is a hung request — `isInFlight` already
 *  treats its market as free — and is dropped here too. */
function livePressSnapshot(nowMs: number): InFlightPress[] {
  const live: InFlightPress[] = [];
  for (const press of _inFlightPresses) {
    if (press.done) continue;
    if (nowMs - press.startedAtMs >= LT2_INFLIGHT_TIMEOUT_MS) {
      _inFlightPresses.delete(press);
      continue;
    }
    live.push(press);
  }
  return live;
}

/**
 * Pull All's snapshot is the authoritative open-order state AT THE PRESS. A
 * hotkey order still waiting for its venue response is not in it yet (an S
 * press especially: its second leg can land late). The pull therefore runs at
 * once for everything known, then, when the presses in flight at the
 * Spacebar settle, once more for the same game, and reports both. The wait is
 * bounded by each press's in-flight ceiling (StreamRift's 8 s rule): a press
 * still unanswered by then is reported as unknown, never waited on forever.
 */
async function pullIncludingInFlight(
  game: Parameters<typeof executeFocusedGamePull>[0],
  inFlight: readonly InFlightPress[],
  onWaiting: (firstMessage: string) => void,
): Promise<{ refused: boolean; message: string }> {
  const first = await executeFocusedGamePull(game);
  if (inFlight.length === 0 || first.status === 'refused') {
    return { refused: first.status === 'refused', message: first.message };
  }
  // The first pass is already done; say so while the in-flight order settles,
  // which can take seconds when its response is slow or lost.
  _pullWaiting = true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    onWaiting(first.message);
    const deadlineMs =
      Math.max(...inFlight.map((p) => p.startedAtMs + LT2_INFLIGHT_TIMEOUT_MS)) - Date.now();
    // Settled either way: a failed placement still ends the wait.
    await Promise.race([
      Promise.all(inFlight.map((p) => p.settled.catch(() => {}))),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, Math.max(0, deadlineMs));
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    _pullWaiting = false;
  }
  const orders = inFlight.flatMap((p) => p.placements.orders);
  const unknown = inFlight.some((p) => p.placements.unknown || !p.done);
  // Exactly those presses' accepted orders, by the ids and tickers they
  // reported (not yet in open orders: the create response has no status), and
  // only where they belong to this game. Anything placed after the press is
  // not the pull's to sweep.
  let late: Awaited<ReturnType<typeof executeFocusedGamePull>> | null = null;
  const parts = [first.message];
  if (orders.length > 0) {
    try {
      late = await executeFocusedGamePull(game, { onlyOrders: orders });
    } catch (err) {
      // Keep the first pass's result on the strip; name the failed second.
      late = { status: 'refused', message: '', requested: 0, submitted: 0, failed: 0 };
      parts.push(
        `the in-flight hotkey order: ${
          err instanceof Error && err.message ? err.message : 'pull failed — check open orders'
        }`,
      );
    }
  }
  if (late != null && late.status !== 'empty' && late.message !== '') {
    parts.push(`the in-flight hotkey order: ${late.message}`);
  }
  if (unknown) {
    parts.push("an in-flight hotkey order's result is unknown — check open orders");
  }
  return {
    // An unknown order is not "pulled": say so in the refusal colour.
    refused: late?.status === 'refused' || unknown,
    message: parts.join('; '),
  };
}

/**
 * Anything a keystroke can be "owned by": a surface that CONSUMES letter keys.
 * Matched with `closest()`, so a key pressed on a menu item inside a portalled
 * menu is owned by that menu even though the menu is nowhere near the card in
 * the DOM.
 *
 * Bare buttons are owners by default. The only exception is a button inside a
 * `data-lt2-hotkey-control` boundary: the explicit LT2 arm, line, perspective,
 * and price controls. Those controls deliberately hand the next A/D press back
 * to the focused trading card. Everything else — toolbar buttons, settings,
 * unrelated menus — refuses while focused, as #609 requires.
 *
 * `data-lt2-owner` opts a single button back OUT of that exemption. The
 * boundary is painted on a whole focused row, so it also covers controls that
 * merely live on the row without being LT2's — #602's fee-uncertainty help
 * button sits on the price pill itself, inside the boundary, and a request for
 * an explanation must never hand A/D back to the card. This is the same defect
 * #650 fenced on the resting-order cancel chip: a control that becomes
 * focusable becomes an A/D launch site. The chip could be fixed structurally
 * (it renders in its own row, outside the boundary); a marker drawn ON the
 * price cannot be, so it says so instead.
 *
 * `[role="dialog"]` appears here (interiors) but plays no part in the
 * page-level modal fence (`modalIsOpen`, the atom's mount count) — a
 * non-modal popover owns its own keystrokes without disabling the board
 * behind it.
 */
const KEY_CONSUMING_OWNER_SELECTOR = [
  'input',
  'textarea',
  'select',
  'dialog',
  '[popover]',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="dialog"]',
].join(',');

const LT2_HOTKEY_CONTROL_SELECTOR = '[data-lt2-hotkey-control]';

/** Per-button opt-out from the boundary exemption above (#602). */
const LT2_OWNER_ATTRIBUTE = 'data-lt2-owner';

/**
 * Overlays that COVER the board suppress hotkeys page-wide even when focus
 * sits on <body>. Read from the mount count atoms/Modal keeps in the focus
 * store — see the header for why no document query backs it.
 */
function modalIsOpen(): boolean {
  return useLt2FocusStore.getState().modalOpenCount > 0;
}

function ownedByInteractive(el: EventTarget | null): boolean {
  if (!(el instanceof Element)) return false;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  // A shadow root hides its interior from closest(): `e.target` retargets to
  // the HOST and `activeElement` reports the host, so an <input> inside a
  // password manager's or an editor extension's overlay reads as ordinary page
  // background and its keystrokes would place orders. We cannot see in, so we
  // refuse. A custom-element tag (the dash is the spec's own marker) is treated
  // the same even with a closed root, where `shadowRoot` reads null.
  if (el.shadowRoot != null || el.tagName.includes('-')) return true;
  if (el.closest(KEY_CONSUMING_OWNER_SELECTOR) != null) return true;

  const button = el.closest('button');
  if (button == null) return false;
  if (button.hasAttribute(LT2_OWNER_ATTRIBUTE)) return true;
  return button.closest(LT2_HOTKEY_CONTROL_SELECTOR) == null;
}

/** Ownership across every reachable view of the event's origin. */
function eventIsOwned(e: KeyboardEvent): boolean {
  // composedPath()[0] is the real innermost target for OPEN shadow roots,
  // where `e.target` has already retargeted to the host.
  const innermost = e.composedPath?.()[0] ?? e.target;
  if (ownedByInteractive(innermost)) return true;
  const target = e.target;
  if (target !== innermost && ownedByInteractive(target)) return true;
  // A real keydown targets the focused element, so activeElement is usually
  // the node just walked — each walk is a `closest()` over a 16-selector
  // union, and a repeat of it answers nothing new.
  const active = document.activeElement;
  if (active === target || active === innermost) return false;
  return ownedByInteractive(active);
}

/**
 * The rows the focused card paints, scoped to the card root useLt2Card
 * published at focus time. Falls back to the document — the exact pre-Deploy-C
 * lookup — when no root is known or the root holds no rows (a re-keyed
 * table can move them), so the scope can only ever narrow the walk, never
 * change which rows are found.
 */
function focusedRows(root: Element | null): Element[] {
  if (root != null) {
    const scoped = root.querySelectorAll('[data-lt2-focused]');
    if (scoped.length > 0) return [...scoped];
  }
  return [...document.querySelectorAll('[data-lt2-focused]')];
}

/**
 * The row DISPLAYING the price A/D would submit, or null when we cannot say
 * which row that is.
 *
 * A focused card is one or two rows: the action row always, plus — when the
 * card is expanded — the ladder row beneath it. Which one bears the price
 * differs, and that is the whole point of this lookup: expanded, the resolver
 * reads the ladder's join bid (SpreadsTotalsTable `lt2TargetsFor`), which is
 * painted BELOW the action row and can sit under the fold while the action row
 * is comfortably on screen. Checking the action row there would certify a price
 * the trader cannot see — the exact clause this guard exists to enforce.
 *
 * So the rows carry three marks: `data-lt2-focused="<cardId>"` says "part of
 * the focused card", `data-lt2-price` says "this row shows the submittable
 * price", and `data-lt2-price-suppressed` says "this row is deliberately
 * unpriced because it is expanded" (read only by `expandedSuppressesPrice`,
 * to pick the ack wording — never to permit a fire).
 * Every unknown refuses: no rows, a row claiming a different card (stale paint
 * or two cards claiming focus — ambiguous focus is a documented pilot stop
 * condition), or anything other than exactly one price row.
 */
function focusedPriceRow(cardId: string, key: 'a' | 'd', root: Element | null): Element | null {
  const rows = focusedRows(root);
  if (rows.length === 0) return null;
  if (rows.some((r) => r.getAttribute('data-lt2-focused') !== cardId)) return null;
  const priced = rows.filter((r) => r.hasAttribute('data-lt2-price'));
  // One priced row serves BOTH keys — the derivative (spread/total) shape,
  // where a single row displays the whole two-action pair. Unchanged.
  if (priced.length === 1) return priced[0];
  // Two priced rows are the game-level moneyline shape: the top/away row is
  // marked "a" and the bottom/home row "d", and the pressed key must prove
  // ITS OWN team's row — A cannot certify the top team's price by finding
  // the bottom team's row on screen. Anything else is ambiguous and refuses.
  if (priced.length === 2) {
    const keyed = priced.filter((r) => r.getAttribute('data-lt2-price') === key);
    return keyed.length === 1 ? keyed[0] : null;
  }
  return null;
}

/** The card exists and is painted, but no single row shows a submittable
 *  price. Two different states produce that, and the trader needs to be told
 *  which one — the ack is the only instruction they get. */
const ACK_NOT_VISIBLE = "Focused card isn't visible — scroll to it or refocus";
const ACK_EXPANDED = 'Card is expanded — collapse it, then retry';

/**
 * #802 — the armed, opted-in Pull All press with NOTHING aimed.
 *
 * Named by the gesture the trader has to make, not by the state they are in,
 * because on this surface the state is invisible: an unaimed board looks
 * exactly like an aimed one until you know to look for the badge. The chip is
 * a bare ⌨ glyph (its wordy form blew the moneyline density budget, see
 * Lt2FocusBadge), so the ack names the glyph the trader must actually hunt
 * for.
 */
const ACK_NEEDS_AIM = "No game aimed — click a game's ⌨ chip, then retry";

/**
 * ── Level-addressed execution (Sniper v2 Phase 2, design §4.1) ─────────────
 *
 * The marks a LEVEL row paints, and why they are a separate namespace from
 * `data-lt2-focused` / `data-lt2-price`.
 *
 * `focusedPriceRow` refuses whenever the focused card paints anything other
 * than one (or, on moneyline, two keyed) priced rows. A twelve-rung ladder that
 * reused those marks would therefore disarm A/D on every Sniper board by
 * construction. So the rungs carry their own four marks and the card-level
 * fence is untouched:
 *
 *   data-lt2-level-card="<cardId>"  this rung belongs to that focused card
 *   data-lt2-level="<n>"            0 = top of book
 *   data-lt2-level-slot="a"|"d"     which book/side the rung is on
 *   data-lt2-level-price="<cents>"  the cents the rung is DISPLAYING
 *
 * The price mark is the point of the whole mechanism. The resolver publishes
 * what it painted (`Lt2HotkeyTarget.levels`) and the row publishes what it
 * rendered; an order fires only when the two agree. One witness could be stale
 * — a resolver that ran against a newer book than the committed DOM, or a row
 * left over from a previous paint — and "the store thinks 31¢" is not evidence
 * that 31¢ is on screen.
 *
 * Every unknown refuses, exactly as the card-level fence does: no row, a row
 * claiming another card, more than one row for the same (card, slot, level), an
 * off-screen row, or a painted price that is not the published one.
 */
const LEVEL_CARD_MARK = 'data-lt2-level-card';
const LEVEL_MARK = 'data-lt2-level';
const LEVEL_SLOT_MARK = 'data-lt2-level-slot';
const LEVEL_PRICE_MARK = 'data-lt2-level-price';

/** Every level row in scope, with the card-root narrowing `focusedRows` uses:
 *  the root can only ever shrink the walk, never change which rows are found. */
function levelRows(root: Element | null): Element[] {
  if (root != null) {
    const scoped = root.querySelectorAll(`[${LEVEL_MARK}][${LEVEL_CARD_MARK}]`);
    if (scoped.length > 0) return [...scoped];
  }
  return [...document.querySelectorAll(`[${LEVEL_MARK}][${LEVEL_CARD_MARK}]`)];
}

/** The ONE row painting (cardId, slot, level), or null when we cannot say which
 *  row that is. Zero and several both refuse — a duplicated rung is ambiguous,
 *  and guessing would fire at a rung the trader did not point at. */
function levelRowFor(
  cardId: string,
  slot: Lt2CursorSlot,
  level: number,
  root: Element | null,
): Element | null {
  const wanted = String(level);
  const matches = levelRows(root).filter(
    (r) =>
      r.getAttribute(LEVEL_CARD_MARK) === cardId &&
      r.getAttribute(LEVEL_SLOT_MARK) === slot &&
      r.getAttribute(LEVEL_MARK) === wanted,
  );
  return matches.length === 1 ? matches[0] : null;
}

const ACK_LEVEL_UNSUPPORTED =
  'This card does not address levels — press 0 for top of book';
const ACK_LEVEL_ABSENT = 'No such level on that side — press 0 for top of book';
const ACK_LEVEL_NOT_VISIBLE = "Cursor level isn't visible — scroll to it or press 0";
const ACK_LEVEL_MISMATCH = 'Ladder and cursor disagree on that level’s price — retry';

type CursorLevelProof =
  | { ok: true; priceCents: number; attack: Lt2AttackTarget | null }
  | { ok: false; ack: string };

/**
 * WHAT this press fires at, proven twice.
 *
 * Engaged whenever the card publishes levels OR the cursor is off top of book.
 * The second half is the fail-closed one: a cursor parked at level 4 on a
 * surface that publishes nothing must REFUSE, never silently fall back to top
 * of book — a key that quietly posts somewhere other than where the cursor is
 * drawn is the defect this whole mechanism exists to prevent.
 */
function resolveCursorLevel(
  cardId: string,
  slot: Lt2CursorSlot,
  level: number,
  root: Element | null,
  target: Lt2HotkeyTarget,
): CursorLevelProof {
  const levels = target.levels;
  if (levels == null) return { ok: false, ack: ACK_LEVEL_UNSUPPORTED };
  const displayed = levels[level];
  if (displayed == null || !Number.isInteger(displayed.priceCents)) {
    return { ok: false, ack: ACK_LEVEL_ABSENT };
  }
  // Level 0 IS the card's top-of-book target. A resolver whose ladder disagrees
  // with its own `priceCents` is internally inconsistent, and the safe reading
  // of an inconsistent card is "we do not know what it is displaying".
  if (level === 0 && target.priceCents != null && displayed.priceCents !== target.priceCents) {
    return { ok: false, ack: ACK_LEVEL_MISMATCH };
  }
  const row = levelRowFor(cardId, slot, level, root);
  if (row == null) return { ok: false, ack: ACK_LEVEL_ABSENT };
  if (!cardIsVisible(row)) return { ok: false, ack: ACK_LEVEL_NOT_VISIBLE };
  const painted = Number(row.getAttribute(LEVEL_PRICE_MARK));
  if (!Number.isInteger(painted) || painted !== displayed.priceCents) {
    return { ok: false, ack: ACK_LEVEL_MISMATCH };
  }
  return {
    ok: true,
    priceCents: displayed.priceCents,
    // At top of book the rung's attack target and the card's ARE the same
    // reading, so a surface that publishes levels without per-rung attack data
    // keeps W/E working exactly as before at level 0 — and refuses below it.
    attack: displayed.attack ?? (level === 0 ? target.attack : null),
  };
}

/** A target label with its "… @ 47¢" price suffix stripped, so a level-
 *  addressed press can re-price it without doubling the suffix. */
function labelBaseOf(label: string): string {
  return label.replace(/ @ \d+¢$/, '');
}

/** Speed-demon D1 — the jump refusals. Each names the state the trader is in
 *  and, where there is one, the gesture that gets them out of it. */
/**
 * ── The sequence composer's keys (Sniper v2 Phase 3, design §5) ───────────
 *
 * Each composer key names ONE relay op, and the key's only job is to FIRE WHAT
 * IS ARMED. Arming — choosing the op, its depth and its per-leg size — belongs
 * to the composer (`stores/sequenceComposerStore`), so nothing here calls
 * `setOp`: a key that armed its own op could turn a glance at the readout into
 * a plan the trader never read.
 *
 * The op is still checked against the armed one, and that check is a SAFETY
 * fence rather than a nicety: `overtake_both` sends the mirrored plan (twice the
 * contracts) and `release_overtake` cancels a resting blocker first, so firing
 * the armed plan from a key that names a different op would place something
 * other than what the trader pressed. A mismatch refuses and names both sides.
 */
const COMPOSER_OP_FOR_ACTION: Partial<Record<LiveHotkeyActionId, SequenceOp>> = {
  overtake: 'overtake',
  'release-overtake': 'release_overtake',
  'overtake-through': 'overtake_through',
  'overtake-both': 'overtake_both',
};

/** Armed, aimed — but the composer holds no plan, so there is nothing to fire.
 *  Named by the gesture that fixes it, like every other ack here. */
const ACK_SEQUENCE_NOT_ARMED =
  'Nothing armed — pick an op on the sequence composer, then retry';
const ACK_SEQUENCE_NEEDS_AIM = "No card aimed — click a card's ⌨ chip, then retry";
const ACK_SEQUENCE_IN_FLIGHT = 'Previous hotkey order still unresolved';

const ACK_JUMP_UNSUPPORTED = 'This card does not track resting orders — jump is not available here';
const ACK_JUMP_NONE = 'No resting order on this card';
const ACK_JUMP_AMBIGUOUS = 'More than one resting order on this side — pull first';
const ACK_JUMP_SAME_PRICE = 'Already at the displayed price';

/**
 * A surface may deliberately suppress its own `data-lt2-price` mark while a
 * card is expanded, when the expanded presentation has no truthful two-action
 * A/D reading. The refusal is correct and the focus is kept; this helper only
 * fixes the wording, since such a card is sitting in front of the trader
 * rather than off-screen. (No current surface paints the mark — moneyline
 * keeps its price line visible through expansion, and the spread/total ladder
 * carries a real price row — but the fence stays: any future suppressing
 * surface gets the honest ack, never a fire.)
 *
 * The suppressing row must itself be ON SCREEN to earn the "collapse it" ack:
 * a card that is both expanded and scrolled away is genuinely not visible, and
 * telling the trader to collapse something they cannot see is the same defect
 * in the other direction. Ambiguity refuses to the default the way
 * `focusedPriceRow` does — this only ever changes which true sentence we say.
 */
function expandedSuppressesPrice(cardId: string, root: Element | null): boolean {
  const rows = focusedRows(root);
  if (rows.length === 0) return false;
  if (rows.some((r) => r.getAttribute('data-lt2-focused') !== cardId)) return false;
  const suppressed = rows.filter((r) => r.hasAttribute('data-lt2-price-suppressed'));
  return suppressed.length === 1 && cardIsVisible(suppressed[0]);
}

/**
 * Rendered, not hidden by CSS, and actually inside the viewport.
 *
 * Answered from the row's IntersectionObserver flag when the flag is FRESH:
 * stamped within `LT2_VISIBILITY_FLAG_MAX_AGE_MS` and after the last
 * scroll/resize the store saw. The observer measured the same two facts
 * (CSS visibility + viewport intersection) on the engine's own rendering
 * step, so a fresh flag is the same verdict without forcing a style+layout
 * flush here. A missing or stale flag — jsdom, an old engine, a row the
 * observer has not caught up with, a scroll since — takes the measured path
 * below, unchanged. A fresh FALSE refuses without measuring: the observer
 * saw the row leave the viewport, and re-measuring would only confirm it.
 */
function cardIsVisible(node: Element): boolean {
  // Fail closed on an engine that cannot answer, rather than skipping the
  // check — an unanswerable question is not a yes.
  if (typeof node.checkVisibility !== 'function') return false;
  const focus = useLt2FocusStore.getState();
  const flag = focus.readRowVisibility(node);
  if (flag != null) {
    const now = Date.now();
    const fresh =
      now - flag.atMs <= LT2_VISIBILITY_FLAG_MAX_AGE_MS &&
      flag.atMs > focus.lastViewportChangeAtMs();
    if (fresh) return flag.visible;
  }
  if (!node.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
  const rect = node.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  return (
    rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < viewportWidth
  );
}

/** Exported for tests. */
export function handleLt2Keydown(e: KeyboardEvent): void {
  // The parity anchor: everything between this stamp and the local ack is
  // what a trader perceives as "hotkey lag" (see lib/trading/lt2Timing).
  // One stamp, both clocks: the monotonic half measures, the epoch half
  // correlates. It is the event's own creation time, not the handler's start,
  // so queueing ahead of this handler counts as lag too.
  const inputAt = eventClockStamp(e);
  const inputAtMs = inputAt.wallMs;
  // #725 — WHICH key fires WHICH action is the trader's setting; what each
  // action then does, and every gate it must clear, is unchanged. `normalize`
  // folds letter case (the #609 Shift fold) and both spellings of the space
  // bar. An unmapped key resolves to null and is ordinary typing.
  const key = normalizeHotkey(e.key);
  // Phase 2 — the binding TOKEN, Shift included, so `f` and `Shift+F` can be
  // two different actions (design §5's composer pair). Every other modifier is
  // still refused outright below; a token is never built for them.
  const token = hotkeyTokenFor(e.key, e.shiftKey);
  // Ownership-gated: a mapping belonging to a previously signed-in trader must
  // never route this keystroke. Falls back to the shipped defaults.
  const hotkeys = readOwnedHotkeys();
  // #725 review 2 — the trader's grants decide which actions exist at all, so
  // they are resolved BEFORE the key is matched. An action the trader has no
  // grant for is not in the binding table, so its key is never consumed here
  // and never reserved in Settings: the two halves of "ungranted actions do
  // not consume their mapped key" now come from the same place.
  const grants: LiveHotkeyGrants = {
    attack: useAuthStore.getState().user?.attackAccess ?? false,
    // #724 — the trader's own opt-in, resolved with the grants for the same
    // reason: an un-opted S is not in the binding table, so it types normally
    // and is free to rebind, exactly like an ungranted W. Owner-bound: an
    // opt-in left on this browser by another trader does not count.
    twoSided: twoSidedOptedIn(
      useDisplaySettings.getState(),
      useAuthStore.getState().user?.id ?? null,
    ),
  };
  const action = actionForKey(hotkeys, token, grants);
  const isAttackKey = action === 'attack-top' || action === 'attack-bottom';
  const isJumpKey = action === 'jump-top' || action === 'jump-bottom';
  const isPullKey = action === 'pull-all';
  // ⚠️ The KEY, not the action. Before #725 the spacebar could only ever be
  // Pull All, so "is this the pull action?" and "is this the spacebar?" were
  // the same question and the browser-default handling below keyed on the
  // former. Remapping split them: bind a POST to the spacebar and every
  // guard that asks about the action gets the wrong answer about the browser.
  const isSpaceKey = key === ' ';
  // Escape is NOT remappable: it is the app-wide dismissal contract
  // (useDismissibleLayer) and #648 fixes it as the focus clear. It is matched
  // separately so no remap can take it and none can shadow it.
  if (action == null && key !== 'Escape') return;
  // Mid-composition the keystroke belongs to the IME, including Escape (which
  // cancels the composition). `keyCode === 229` is the pre-isComposing signal
  // some IMEs still send.
  if (e.isComposing || e.keyCode === 229) return;

  const owned = eventIsOwned(e);
  const focus = useLt2FocusStore.getState();

  if (key === 'Escape') {
    if (owned) return; // Escape belongs to whatever the trader is in
    // Phase 2 (design §8 "Esc: disarm + clear cursor"): the thing Escape
    // disarms is the AIM — the focused card, the level cursor, and any armed
    // op's ghost legs. It deliberately does NOT drop one-click arming: this
    // listener is mounted app-wide, and an Escape pressed to dismiss a tooltip
    // on the dashboard must not silently take a trader's arm away. Clearing
    // the focus already resets the cursor; the explicit calls cover the case
    // where nothing is focused but a cursor/plan was left behind.
    if (focus.focusedCardId != null) focus.clearFocus();
    focus.resetCursor();
    focus.clearGhostLegs();
    return;
  }
  // Escape was the only unmapped key with a job. Everything past here needs a
  // mapped action, so an unbound key stays ordinary typing.
  if (action == null) return;

  const oneClick = useOneClickStore.getState();
  if (!oneClick.armed) return;
  // #802 — READ here, DECIDED below. This used to be `if (cardId == null)
  // return`, sitting above every silent guard and above the shared
  // preventDefault — which is exactly why an armed, opted-in Pull All press
  // with nothing aimed fell through to the browser and scrolled the pane while
  // saying nothing. The decision now lives at the consumed point, so a press
  // that was never an order attempt (typing, a modal, a background tab, a held
  // key) still says nothing at all.
  const cardId = focus.focusedCardId;
  // Spacebar consumes only when its own opt-in setting is on; otherwise it
  // stays exactly the browser key it always was (scroll, button activation).
  const pullEnabled = isPullKey && useDisplaySettings.getState().lt2PullAllEnabled;
  if (isPullKey && !pullEnabled) return;
  if (e.repeat) {
    // Held key must not machine-gun. A held SPACE additionally must not start
    // scrolling the board mid-hold after its first press was consumed — true
    // for ANY action bound to it, not just Pull All.
    //
    // ⚠️ "after its first press was consumed" is a CONDITION, not a preamble.
    // #802 moved the no-focus decision below this branch, which briefly made a
    // held space claimed even where the first press is deliberately left to
    // the browser — a post bound to Space with nothing aimed, which scrolled
    // once and then stopped. The repeat must claim exactly what the first
    // press claims: a focused card (any action), or the pull action, which is
    // consumed unaimed precisely so it can answer.
    const wouldConsume =
      (cardId != null || isPullKey) &&
      !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey &&
      !modalIsOpen() &&
      document.visibilityState === 'visible';
    if (isSpaceKey && !owned && wouldConsume) e.preventDefault();
    return;
  }
  // Ctrl / Alt / Meta chords belong to the browser and the OS and are never
  // bindable — unchanged since #609. SHIFT is now conditional, and narrowly:
  // the press got here by matching a `shift+` token, and the action it matched
  // must ALSO declare `allowsShift`. Two gates rather than one because the
  // token half can be reached by a hand-edited mapping, and a chord landing on
  // an ordinary order action is exactly the surprise #609 closed.
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.shiftKey && LIVE_HOTKEY_REGISTRY[action].allowsShift !== true) return;

  // Attack exists only for attack-entitled traders. That is now enforced one
  // step earlier — `actionForKey` cannot return an attack action without the
  // grant — so for everyone else the key stays exactly what it was before V3:
  // ordinary typing, never a dead-key ack for a capability they do not have.
  // Kept as a second, local assertion because this is the branch that submits
  // an aggressive order, and a grant check is not something to hold in one
  // place only.
  if (isAttackKey && !grants.attack) return;

  const keyLabel = hotkeyDisplayLabel(key);
  // M3b latency probe: compare the passive top/bottom hotkey path against
  // mouse entry on the same marks. Attack and Pull All have separate timing
  // contracts and must not leave this passive-only probe open.
  if (action === 'passive-top' || action === 'passive-bottom') {
    probeStart(
      action === 'passive-top' ? 'hotkey-a' : 'hotkey-d',
      `Hotkey ${keyLabel}`,
    );
  }
  // Armed, focused, deliberate — and still refused. Silence here is the one
  // silence that misleads: the trader is mid-flow and cannot tell a refusal
  // from a sent order. Everywhere else an owned keystroke is simply not an
  // order attempt and says nothing.
  if (owned) {
    // A space typed into an input (or activating a focused button) is
    // ordinary browser behavior, never an order gesture — fully silent.
    // Keyed on the SPACEBAR, not on Pull All: a trader who binds a post to
    // space would otherwise get a spurious refusal every time they typed a
    // space into a search box.
    if (isSpaceKey) return;
    // "Click the card, then retry" only makes sense to a trader who HAS a
    // card. With nothing aimed there is no order attempt to explain, so this
    // stays the silence it was before #802 moved the no-focus decision below
    // this branch.
    if (cardId == null) return;
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      'Focus is on another control — click the card, then retry',
    );
    return;
  }
  if (modalIsOpen()) return;
  // Nobody is looking at a backgrounded tab, so nothing there is deliberate.
  if (document.visibilityState !== 'visible') return;

  // From here the press is CONSUMED: armed, aimed, un-owned, no modal, visible
  // tab. The browser must not ALSO act on it — even when the action then
  // refuses, because the refusal is the response.
  //
  // This used to live inside the Pull All branch alone, which was correct only
  // while the spacebar was Pull All's exclusive key. A post bound to the
  // spacebar scrolled the board on every truthful refusal, and punctuation
  // keys carry their own browser defaults (`/` opens quick-find in Firefox).
  // Every silent guard has already returned above, so nothing that was
  // deliberately left as ordinary typing is affected.

  // ── Navigation (Phase 2) ─────────────────────────────────────────────────
  // Resolved BEFORE the order path because none of these needs a price, a
  // slot, or a visible price row: they move WHERE the next order key would
  // aim, and that aim is then proved from scratch when an order key fires.
  // Nothing here can reach the venue — `navigation` and `inputMethod` are
  // mutually exclusive by registry invariant.
  const navigation = LIVE_HOTKEY_REGISTRY[action].navigation;
  if (navigation != null) {
    // The cursor belongs to a CARD, so a cursor key with nothing aimed is the
    // same silent no-op A/D/W/E are: no badge anywhere means no target, and a
    // receipt line for a cursor nudge would be pure noise. The band's view keys
    // (Z, `) are card-independent and consume unaimed.
    const needsCard = navigation.kind !== 'cycle-market' && navigation.kind !== 'toggle-dock';
    if (needsCard && cardId == null) return;
    e.preventDefault();
    switch (navigation.kind) {
      case 'cursor-move':
        focus.moveCursor(navigation.delta);
        break;
      case 'cursor-level':
        focus.setCursorLevel(navigation.level);
        break;
      case 'book-move':
        focus.moveCursorSlot(navigation.delta);
        break;
      case 'cycle-market':
        focus.cycleMarketTab();
        break;
      case 'toggle-dock':
        focus.toggleDockPage();
        break;
    }
    return;
  }

  // ── The latency dump (Phase 4, design §6) ────────────────────────────────
  // `Y` copies the HUD's own ring to the clipboard. It reaches no venue and
  // needs no card, so it sits beside the band's view keys rather than in the
  // order path — and it calls the HUD's exported dump rather than re-deriving
  // the CSV, so the clipboard and the screen can never disagree.
  if (action === 'latency-dump') {
    e.preventDefault();
    void dumpLatencyCsv();
    return;
  }

  // ── The composer's armed sequence (Phase 3, design §5) ───────────────────
  // A sequence is a real-money write, so it runs EVERY fence an A/D press runs
  // — armed (above), un-owned (above), no modal, visible tab (above), a focused
  // card, a live market, a visible price row on the cursor's book, the cursor's
  // rung proven by both witnesses, the per-market in-flight lock — and then the
  // store's own gate + pacing fences inside `fire()`. The key adds nothing to
  // what the composer may do; it only pulls the trigger on what is armed.
  const composerOp = COMPOSER_OP_FOR_ACTION[action];
  if (composerOp != null) {
    // Claimed from here: a deliberate sequence press gets an ANSWER, never a
    // browser default plus silence — the refusal is the response.
    e.preventDefault();
    const composerLabel = `Hotkey ${hotkeyDisplayLabel(token)}`;
    if (cardId == null) {
      oneClick.rejectLocal(composerLabel, ACK_SEQUENCE_NEEDS_AIM);
      return;
    }
    const composer = useSequenceComposerStore.getState();
    // "Nothing armed" is the cursor/plan question, and it is asked FIRST so a
    // trader who has not armed anything is told that rather than something
    // about the ladder.
    if (composer.cursor == null || composer.plan == null) {
      oneClick.rejectLocal(composerLabel, ACK_SEQUENCE_NOT_ARMED);
      return;
    }
    if (composer.op !== composerOp) {
      oneClick.rejectLocal(
        composerLabel,
        `${OP_LABEL[composer.op]} is armed, not ${OP_LABEL[composerOp]} — press its key or re-arm`,
      );
      return;
    }
    const seqTargets = focus.resolveFocused();
    if (!seqTargets) {
      oneClick.rejectLocal(composerLabel, 'Focused market is gone — refocus a card');
      focus.clearFocus();
      return;
    }
    // The composer acts on the book the CURSOR is drawn on (design §4.1), so
    // that is the slot whose displayed price row the fence must prove — the
    // same proof A/D run on their own slot.
    const seqSlot = focus.cursor.slot;
    const seqAmbiguous = focus.focusPaintAmbiguous;
    const seqRoot = seqAmbiguous ? null : focus.focusedCardRoot();
    const seqPriceRow = seqAmbiguous ? null : focusedPriceRow(cardId, seqSlot, seqRoot);
    if (seqPriceRow == null || !cardIsVisible(seqPriceRow)) {
      oneClick.rejectLocal(
        composerLabel,
        seqPriceRow == null && !seqAmbiguous && expandedSuppressesPrice(cardId, seqRoot)
          ? ACK_EXPANDED
          : ACK_NOT_VISIBLE,
      );
      return;
    }
    const seqTarget: Lt2HotkeyTarget | null = seqSlot === 'a' ? seqTargets.a : seqTargets.d;
    if (!seqTarget || seqTarget.priceCents == null) {
      oneClick.rejectLocal(composerLabel, 'No displayed price on that side');
      return;
    }
    if (seqTarget.availability !== 'active') {
      oneClick.rejectLocal(
        seqTarget.label,
        `${lineRefusalReason(seqTarget, seqTarget.availability)} — order not sent`,
      );
      return;
    }
    // Two witnesses for the rung the plan starts at, exactly as a level-
    // addressed A/D press: the card must publish a displayed level there and
    // the DOM row must be painting the same cents. A card that addresses no
    // levels refuses as unsupported — the composer's ladder comes from those
    // published rungs, so there is nothing truthful to fire at without them.
    const seqProof = resolveCursorLevel(
      cardId,
      seqSlot,
      focus.cursor.level,
      seqRoot,
      seqTarget,
    );
    if (!seqProof.ok) {
      oneClick.rejectLocal(seqTarget.label, seqProof.ack);
      return;
    }
    const seqTicker = seqTarget.book?.marketTicker ?? '';
    const seqInFlightKey = inFlightKeyFor(seqTicker, cardId);
    const seqNowMs = Date.now();
    if (isInFlight(seqInFlightKey, seqNowMs)) {
      oneClick.rejectLocal(composerLabel, ACK_SEQUENCE_IN_FLIGHT);
      return;
    }
    // The timing tag names the ACTION, never the key — a remap must not rewrite
    // what a past latency row says it measured (the same rule the `__lt2` audit
    // stamp follows). The store's `fire()` re-checks every gate and the pacing
    // window; this call only says "now".
    const release = markInFlight(seqInFlightKey, seqNowMs);
    void fireArmedSequence({
      inputMethod: `composer-${composerOp}`,
      inputAtMs,
    }).finally(release);
    return;
  }

  // ── The unaimed press (#802) ─────────────────────────────────────────────
  // Deliberately asymmetric, and the asymmetry is the point. A/D/W/E aim at
  // ONE card and announce it: an unaimed board simply has no badge anywhere,
  // and their press stays the silent, UNCLAIMED no-op it has always been —
  // `Lt2HotkeyListener.test.tsx`'s "a press we deliberately ignore is NOT
  // claimed" pins that for a post bound to the spacebar. Pull All is the one
  // action whose name already implies its own target ("the current game"), so
  // it is the one press a trader can reasonably believe they have aimed. It
  // gets an answer instead of a scroll.
  //
  // Keyed on the ACTION, never on the spacebar: bind a post to Space and it
  // must keep falling through here untouched.
  if (cardId == null) {
    if (!isPullKey) return;
    // Claimed — the refusal IS the response, so the browser must not also act
    // on the key. Without this the instruction appears AND the pane pages
    // away from the chip the instruction just told the trader to click.
    e.preventDefault();
    oneClick.rejectLocal('Pull All', ACK_NEEDS_AIM);
    return;
  }

  e.preventDefault();

  if (isPullKey) {
    const targets = focus.resolveFocused();
    if (!targets) {
      oneClick.rejectLocal('Pull All', 'Focused market is gone — refocus a card');
      focus.clearFocus();
      return;
    }
    const scope = targets.pullScope;
    if (scope == null) {
      oneClick.rejectLocal('Pull All', 'This card cannot anchor a game-scoped pull');
      return;
    }
    if (isPullAllPending() || _pullWaiting) {
      oneClick.rejectLocal('Pull All', 'Pull All already pending — wait for the result');
      return;
    }
    oneClick.noteAck('pending', 'Pull All', `cancelling resting orders on ${targets.cardLabel}…`);
    void pullIncludingInFlight(scope.game, livePressSnapshot(Date.now()), (firstMessage) =>
      oneClick.noteAck(
        'pending',
        'Pull All',
        `${firstMessage}; waiting for the in-flight hotkey order…`,
      ),
    ).then(
      (outcome) => {
        oneClick.noteAck(outcome.refused ? 'rejected' : 'accepted', 'Pull All', outcome.message);
      },
      (err: unknown) => {
        oneClick.noteAck(
          'rejected',
          'Pull All',
          err instanceof Error && err.message ? err.message : 'Pull failed — check open orders',
        );
      },
    );
    return;
  }

  // Armed, focused, entitled — but Attack itself is disarmed. This is the
  // deliberate W/E press the packet's first acceptance test names: visible
  // blocked/disarmed feedback, never a silent drop and never an order.
  if (isAttackKey && !useAttackStore.getState().attackArmed) {
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      `Attack is disarmed — arm Attack to use ${hotkeyPairLabel(
        hotkeys,
        'attack-top',
        'attack-bottom',
        grants,
      )}`,
    );
    return;
  }

  const targets = focus.resolveFocused();
  if (!targets) {
    oneClick.rejectLocal(`Hotkey ${keyLabel}`, 'Focused market is gone — refocus a card');
    focus.clearFocus();
    return;
  }

  // ── Two-sided S (#724) ───────────────────────────────────────────────────
  // Branched BY NAME before the slot is resolved: S is not a single-sided
  // order, and its fence must prove BOTH displayed rows the way A and D each
  // prove one. Every refusal below is truthful (armed, aimed, deliberate), and
  // nothing here may guess a side, a price or a size.
  if (action === 'two-sided') {
    fireTwoSided({ cardId, targets, focus, oneClick, keyLabel, inputAt });
    return;
  }

  // The card still exists, but the row showing the price must be on screen to
  // be fired at. Focus is KEPT either way — scrolling back or collapsing is
  // the fix, not a re-click.
  // W shares A's slot (top/left), E shares D's (bottom/right): the attack
  // fires on the same book/side whose displayed row the fence must prove.
  // The slot is a property of the ACTION, so a remap cannot move an order to
  // a side other than the one whose displayed price the fence just proved.
  // FAIL-CLOSED, and deliberately not `?? 'a'`. An action with no slot is not
  // a single-sided order — Pull All today, the two-sided S action (#724) next.
  // Defaulting a missing slot to the top side would turn a press meant for
  // BOTH sides into one silent top-side order on a live venue. Nothing here
  // may guess which side the trader meant.
  const actionSlot = LIVE_HOTKEY_REGISTRY[action].slot;
  if (actionSlot == null) return;
  const slot: 'a' | 'd' = actionSlot;
  // Some OTHER card last painted itself focused — a foreign mark is in the
  // DOM. The document query used to find that row and refuse; the store now
  // knows it from the card's own commit, and the refusal is the same one:
  // no trusted price row, the "not visible" ack, focus kept.
  const ambiguous = focus.focusPaintAmbiguous;
  const root = ambiguous ? null : focus.focusedCardRoot();
  const priceRow = ambiguous ? null : focusedPriceRow(cardId, slot, root);
  if (priceRow == null || !cardIsVisible(priceRow)) {
    // A row that exists but is off-screen is never "expanded" for ack
    // purposes — the price it would fire at is the one the trader can't see.
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      priceRow == null && !ambiguous && expandedSuppressesPrice(cardId, root)
        ? ACK_EXPANDED
        : ACK_NOT_VISIBLE,
    );
    return;
  }
  const target: Lt2HotkeyTarget | null = slot === 'a' ? targets.a : targets.d;
  if (!target || target.priceCents == null) {
    oneClick.rejectLocal(`Hotkey ${keyLabel}`, 'No displayed price on that side');
    return;
  }
  if (target.availability !== 'active') {
    oneClick.rejectLocal(
      target.label,
      `${lineRefusalReason(target, target.availability)} — order not sent`,
    );
    return;
  }
  // ── The rung this press fires at (Phase 2) ───────────────────────────────
  // Engages only when the focused card publishes an addressable ladder or the
  // cursor has been moved off top of book. Every pre-Phase-2 surface publishes
  // neither and keeps its exact behaviour: `cursorLevel === 0`, `levels`
  // absent, nothing below runs.
  const cursorLevel = focus.cursor.level;
  let firePriceCents = target.priceCents;
  let fireAttack = target.attack;
  if (target.levels != null || cursorLevel > 0) {
    const proof = resolveCursorLevel(cardId, slot, cursorLevel, root, target);
    if (!proof.ok) {
      oneClick.rejectLocal(target.label, proof.ack);
      return;
    }
    firePriceCents = proof.priceCents;
    fireAttack = proof.attack;
  }
  // Every ack and receipt names the rung it actually acted on. A level-addressed
  // order that still read "… @ 47¢" (top of book) would be a receipt that
  // disagrees with the order — the same class of lie the price marks prevent.
  const labelBase = labelBaseOf(target.label);
  const levelSuffix = cursorLevel > 0 ? ` · L${cursorLevel}` : '';
  const fireLabel =
    cursorLevel > 0 ? `${labelBase} @ ${firePriceCents}¢${levelSuffix}` : target.label;

  const ticker = target.book?.marketTicker ?? '';
  const inFlightKey = inFlightKeyFor(ticker, cardId);
  const nowMs = Date.now();
  if (isInFlight(inFlightKey, nowMs)) {
    oneClick.rejectLocal(fireLabel, 'Previous hotkey order still unresolved');
    return;
  }

  probeMark('fence-pass');
  // The durable `__lt2` audit stamp is a property of the ACTION, never of the
  // key: `hotkey-a` keeps meaning "the passive top/left action" for every order
  // ever placed, so remapping cannot rewrite what past records say they were
  // placed by. An action carrying no stamp places no order and must not reach
  // the intent builders below.
  const inputMethod = LIVE_HOTKEY_REGISTRY[action].inputMethod;
  if (inputMethod == null) return;

  if (isAttackKey) {
    // The resolver computed the bounded target from the displayed book (best
    // same-side bid + accepted depth). Null means there is nothing truthful
    // to attack: no displayed bid, a fee-hidden price, or a target outside
    // 1..99 — refuse with the reason, exactly like a missing passive price.
    // At the cursor's rung, not only at top of book: the reference is the level
    // the card says it painted there (`Lt2DisplayedLevel.attack`), never a
    // price this handler derived. A rung with no published attack target
    // refuses exactly like a card with none.
    const attack = fireAttack;
    if (attack == null) {
      oneClick.rejectLocal(
        `Hotkey ${keyLabel}`,
        'No attackable level — needs a displayed bid with the level above in range',
      );
      return;
    }
    const intent: LiveOrderIntent = {
      ticker,
      side: target.side,
      priceCents: attack.targetPriceCents,
      postOnly: false,
      label: `ATT ${labelBase} @ ${attack.targetPriceCents}¢${levelSuffix}`,
      inputMethod,
      surface: targets.surface,
      marketKind: targets.marketKind,
      period: targets.period,
      line: targets.line,
      focusedCardId: focus.focusedCardId,
      displayedPriceMs: nowMs,
      inputAtMs,
      inputAtClock: inputAt,
      attack: { depth: attack.depth, referencePriceCents: attack.referencePriceCents },
    };
    trackInFlight([inFlightKey], nowMs, (report) =>
      oneClick.submitAttackIntent(intent, target.book, report),
    );
    return;
  }

  const intent: LiveOrderIntent = {
    ticker,
    side: target.side,
    priceCents: firePriceCents,
    postOnly: target.postOnly,
    label: fireLabel,
    inputMethod,
    surface: targets.surface,
    marketKind: targets.marketKind,
    period: targets.period,
    line: targets.line,
    focusedCardId: focus.focusedCardId,
    displayedPriceMs: nowMs,
    inputAtMs,
    inputAtClock: inputAt,
  };

  if (isJumpKey) {
    // The resolver names the ONE resting order on this slot from the same
    // own-order rows its chips render — so the order the jump moves is the
    // order the trader can see. Three refusals, each truthful: the surface
    // does not resolve resting orders at all (absent), none or several
    // (null, with the ambiguity flag), or it already sits at the displayed
    // price (a jump nowhere would only re-queue it).
    if (target.resting === undefined) {
      oneClick.rejectLocal(fireLabel, ACK_JUMP_UNSUPPORTED);
      return;
    }
    if (target.restingAmbiguous) {
      oneClick.rejectLocal(fireLabel, ACK_JUMP_AMBIGUOUS);
      return;
    }
    const resting = target.resting;
    if (resting == null) {
      oneClick.rejectLocal(fireLabel, ACK_JUMP_NONE);
      return;
    }
    // Against the rung the cursor addressed: a jump to the level the order
    // already rests at would only re-queue it, wherever on the ladder that is.
    if (firePriceCents === resting.priceCents) {
      oneClick.rejectLocal(fireLabel, ACK_JUMP_SAME_PRICE);
      return;
    }
    const jump: LiveJumpIntent = {
      ...intent,
      replaceOrderId: resting.orderId,
      prevPriceCents: resting.priceCents,
      prevRemaining: resting.remainingCount,
    };
    trackInFlight([inFlightKey], nowMs, (report) =>
      oneClick.submitJumpIntent(jump, target.book, report),
    );
    return;
  }

  trackInFlight([inFlightKey], nowMs, (report) =>
    oneClick.submitIntent(intent, target.book, report),
  );
}

/** Test seam: drop every in-flight entry (the map is module state). */
export function _resetLt2InFlightForTests(): void {
  _inFlight.clear();
  _inFlightPresses.clear();
  _pullWaiting = false;
}

/**
 * #724 — the two-sided press, after the shared gates have run.
 *
 * Membership is EXACTLY the card's A and D passive targets — the resolver only
 * ever yields those two, so a three-outcome (soccer) card can never grow a
 * draw leg here. What this proves, in order, and refuses truthfully when it
 * cannot:
 *
 *   1. both price rows are painted and on screen (moneyline: the two team
 *      rows; a derivative: the one row that shows both actions);
 *   2. the card's targets pass `twoSidedReadiness` — two outcomes, both
 *      prices shown on active lines, both prices bid joins, a real pair, and
 *      bids totalling under 100¢ (the header readout asks the same function);
 *   3. (StreamRift) the level cursor is at top of book, and on a surface that
 *      publishes levels both legs pass the same two-witness level-0 proof an
 *      A/D press runs — S never posts somewhere the cursor is not;
 *   4. neither leg's market has a hotkey order in flight (per market, as A/D);
 *   5. no earlier S press still has a side whose outcome is unknown.
 *
 * Both legs are sent POST-ONLY. On moneyline that differs from A and D, which
 * keep the pill's postOnly:false for mouse parity: a two-sided press exists to
 * rest on both books, so a leg that would now cross is refused, not filled.
 *
 * The legs keep their own stamps (hotkey-a / hotkey-d) because that is what
 * each order IS; the store adds the shared group stamp that says they were
 * fired together. Size is the one-click size, once, frozen in the store.
 */
function fireTwoSided(args: {
  cardId: string;
  targets: Lt2CardTargets;
  focus: ReturnType<typeof useLt2FocusStore.getState>;
  oneClick: ReturnType<typeof useOneClickStore.getState>;
  keyLabel: string;
  inputAt: Lt2ClockStamp;
}): void {
  const { cardId, targets, focus, oneClick, keyLabel, inputAt } = args;
  const hotkey = `Hotkey ${keyLabel}`;

  // The same row fence A/D run, on both rows: the store's paint-ambiguity
  // flag first (a foreign focus mark refuses), then both rows scoped to the
  // focused card's root.
  const ambiguous = focus.focusPaintAmbiguous;
  const root = ambiguous ? null : focus.focusedCardRoot();
  const rowA = ambiguous ? null : focusedPriceRow(cardId, 'a', root);
  const rowD = ambiguous ? null : focusedPriceRow(cardId, 'd', root);
  if (rowA == null || rowD == null || !cardIsVisible(rowA) || !cardIsVisible(rowD)) {
    oneClick.rejectLocal(
      hotkey,
      (rowA == null || rowD == null) && !ambiguous && expandedSuppressesPrice(cardId, root)
        ? ACK_EXPANDED
        : ACK_NOT_VISIBLE,
    );
    return;
  }
  // The level cursor (Sniper v2): S is a top-of-book press. With the cursor
  // parked below it, posting at top of book would place orders somewhere the
  // cursor is not drawn — the defect the cursor rule exists to prevent — and
  // there is no two-rung S, so it refuses and names the way back.
  if (focus.cursor.level > 0) {
    oneClick.rejectLocal(hotkey, `${keyLabel} posts at top of book — press 0, then retry`);
    return;
  }
  // Everything that is a property of the card's targets is decided by the
  // same predicate the header readout uses, so the header can only name S
  // when a press would get past these checks.
  const ready = twoSidedReadiness(targets, keyLabel);
  if (!ready.ok) {
    oneClick.rejectLocal(ready.legLabel ?? hotkey, ready.reason);
    return;
  }
  // A surface that publishes levels (/live, the Sniper cockpit) proves each
  // leg's top-of-book rung with both witnesses — the resolver's level 0 and
  // the row painting the same cents — exactly as an A/D press at level 0.
  for (const [slot, leg] of [['a', ready.a], ['d', ready.d]] as const) {
    if (leg.target.levels == null) continue;
    const proof = resolveCursorLevel(cardId, slot, 0, root, leg.target);
    if (!proof.ok) {
      oneClick.rejectLocal(leg.target.label, proof.ack);
      return;
    }
  }
  // Per-market in-flight lock, both legs' markets (one key on a spread/total
  // card, whose two legs share the line's market).
  const inFlightKeys = [
    inFlightKeyFor(ready.a.ticker, cardId),
    inFlightKeyFor(ready.d.ticker, cardId),
  ];
  const nowMs = Date.now();
  if (inFlightKeys.some((k) => isInFlight(k, nowMs))) {
    oneClick.rejectLocal(hotkey, 'Previous hotkey order still unresolved');
    return;
  }
  // An earlier S press with a side whose outcome is still unknown blocks the
  // next one: resending both legs could double a leg that is already live
  // (#724: never blind-retry an unknown leg). A and D stay available.
  const held = oneClick.twoSidedHoldReason();
  if (held != null) {
    oneClick.rejectLocal(hotkey, held);
    return;
  }

  const stampA = LIVE_HOTKEY_REGISTRY['passive-top'].inputMethod;
  const stampD = LIVE_HOTKEY_REGISTRY['passive-bottom'].inputMethod;
  if (stampA == null || stampD == null) return; // registry invariant; never true
  // Every S leg is POST-ONLY, whatever the pill would submit on a click: the
  // readiness check proved each price is a bid join, so post-only only turns
  // a book that moved into a refusal instead of a crossing fill.
  const leg = (l: TwoSidedLeg, inputMethod: typeof stampA): LiveOrderIntent => ({
    ticker: l.ticker,
    side: l.target.side,
    priceCents: l.priceCents,
    postOnly: true,
    label: l.target.label,
    inputMethod,
    surface: targets.surface,
    marketKind: targets.marketKind,
    period: targets.period,
    line: targets.line,
    focusedCardId: focus.focusedCardId,
    displayedPriceMs: nowMs,
    inputAtMs: inputAt.wallMs,
    inputAtClock: inputAt,
  });
  trackInFlight(inFlightKeys, nowMs, (report) =>
    oneClick.submitTwoSidedIntent({
      // On a three-way game the pair is NO on both teams, which both pay on a
      // draw; the strip says so on every press, not only in a tooltip.
      label: `${keyLabel} ${targets.cardLabel}${targets.threeWay === true ? ' (both NO)' : ''}`,
      legs: [leg(ready.a, stampA), leg(ready.d, stampD)],
      books: [ready.a.target.book, ready.d.target.book],
      report,
    }),
  );
}

export function Lt2HotkeyListener() {
  useEffect(() => {
    // `handleLt2Keydown` MUST stay a stable module-level reference. Both
    // hosting surfaces mount this component, and addEventListener dedupes on
    // (type, listener) — so even if they ever mounted together there is one
    // registration and one submission per press. Rewriting this as an inline
    // arrow looks harmless and would quietly create a double-registration
    // (hence double-submit) hazard. The surfaces are exclusive routes today,
    // which is also why the shared cleanup below cannot strand the survivor.
    window.addEventListener('keydown', handleLt2Keydown);
    // Disarming clears the focus: an unfocused, disarmed page is the only
    // safe idle state, and the ring must never suggest A/D are live.
    const unsubscribe = useOneClickStore.subscribe((state, prev) => {
      if (prev.armed && !state.armed) useLt2FocusStore.getState().clearFocus();
    });
    return () => {
      window.removeEventListener('keydown', handleLt2Keydown);
      unsubscribe();
      useLt2FocusStore.getState().clearFocus();
    };
  }, []);
  return null;
}
